import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { format, parseISO } from 'date-fns';

const COLORS = {
  primary: "#F15A24",
  primaryLight: "#FF7A45",
  secondary: "#FFC20E",
  secondaryLight: "#FFD95E",
  lightGray: "#F8F8F8",
  darkGray: "#2D2D2D",
  white: "#FFFFFF",
  black: "#000000"
};

const MedicalReportManagement = () => {
  // State for upload form
  const [phoneNumber, setPhoneNumber] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  
  // State for search functionality
  const [searchPhoneNumber, setSearchPhoneNumber] = useState("");
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [activeTab, setActiveTab] = useState("upload"); // 'upload' or 'search'
  
  const apiUrl = import.meta.env.VITE_API_URL;

  // Handle file upload
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  // Handle report upload submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");
    setIsLoading(true);

    const formData = new FormData();
    formData.append('phone_number', phoneNumber);
    formData.append('title', title);
    formData.append('description', description);
    if (file) formData.append('file', file);

    try {
      const response = await axios.post(
        `${apiUrl}/medical-reports/upload/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.status === 201) {
        setSuccessMessage("Medical report uploaded successfully!");
        // Reset form
        setPhoneNumber("");
        setTitle("");
        setDescription("");
        setFile(null);
        document.getElementById('file-upload').value = "";
        
        // Refresh reports if we're viewing the same patient
        if (searchPhoneNumber === phoneNumber) {
          fetchReports(phoneNumber);
        }
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Failed to upload medical report. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle report download
  const handleDownloadReport = (reportUrl) => {
    if (reportUrl) {
      window.open(reportUrl, "_blank");
    } else {
      alert("No report available to download.");
    }
  };

  // Handle report deletion
  const handleDeleteReport = async (reportId) => {
    if (window.confirm("Are you sure you want to delete this report?")) {
      try {
        await axios.delete(`${apiUrl}/medical-reports/${reportId}/`);
        setSuccessMessage("Report deleted successfully");
        fetchReports(searchPhoneNumber); // Refresh the list
      } catch (error) {
        setErrorMessage("Failed to delete report");
      }
    }
  };

  // Fetch reports by phone number
  const fetchReports = async (phone) => {
    if (!phone) return;
    
    setIsLoading(true);
    setErrorMessage("");
    
    try {
      const response = await axios.get(
        `${apiUrl}/medical-reports/?phone_number=${phone}`
      );

      if (response.data && response.data.length > 0) {
        setReports(response.data);
        setFilteredReports(response.data);
      } else {
        setReports([]);
        setFilteredReports([]);
        setErrorMessage("No reports found for this phone number");
      }
    } catch (error) {
      setErrorMessage("Error fetching reports. Please try again.");
      setReports([]);
      setFilteredReports([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchReports(searchPhoneNumber);
  };

  // Filter reports by date range
  const handleFilter = () => {
    if (!startDate && !endDate) {
      setFilteredReports(reports);
      return;
    }

    const filtered = reports.filter(report => {
      const reportDate = new Date(report.created_at);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      if (start && end) {
        return reportDate >= start && reportDate <= end;
      } else if (start) {
        return reportDate >= start;
      } else if (end) {
        return reportDate <= end;
      }
      return true;
    });

    setFilteredReports(filtered);
  };

  useEffect(() => {
    handleFilter();
  }, [startDate, endDate, reports]);

  return (
    <div className="min-h-screen flex flex-col" 
      style={{ 
        paddingTop: "80px",
        minHeight: "calc(100vh - 80px)"
      }}
    >
      {/* Hero Section */}
      <div 
        className="w-full py-16 px-8 text-center relative overflow-hidden"
        style={{ backgroundColor: COLORS.primary }}
      >
        <div className="absolute inset-0 z-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80" 
            alt="Medical background"
            className="w-full h-full object-cover"
          />
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10"
        >
          <h1 className="text-4xl font-bold text-white mb-4">
            Medical Report Management
          </h1>
          <p className="text-xl text-white max-w-2xl mx-auto">
            Upload and manage patient medical reports
          </p>
        </motion.div>
      </div>

      {/* Main Content */}
      <div className="flex-1 bg-white py-12 px-8">
        <div className="max-w-6xl mx-auto">
          {/* Tab Navigation */}
          <div className="flex border-b mb-8">
            <button
              className={`py-2 px-4 font-medium ${activeTab === 'upload' ? 'border-b-2' : ''}`}
              style={{
                color: activeTab === 'upload' ? COLORS.primary : COLORS.darkGray,
                borderColor: activeTab === 'upload' ? COLORS.primary : 'transparent'
              }}
              onClick={() => setActiveTab('upload')}
            >
              Upload Reports
            </button>
            <button
              className={`py-2 px-4 font-medium ${activeTab === 'search' ? 'border-b-2' : ''}`}
              style={{
                color: activeTab === 'search' ? COLORS.primary : COLORS.darkGray,
                borderColor: activeTab === 'search' ? COLORS.primary : 'transparent'
              }}
              onClick={() => setActiveTab('search')}
            >
              View/Manage Reports
            </button>
          </div>

          {/* Upload Tab */}
          {activeTab === 'upload' && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white p-8 rounded-xl shadow-lg border border-gray-100"
            >
              <h2 className="text-2xl font-bold mb-6" style={{ color: COLORS.darkGray }}>
                Upload Medical Report
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: COLORS.darkGray }}
                    htmlFor="phone-number"
                  >
                    Patient Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone-number"
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:outline-none"
                    style={{
                      borderColor: '#E5E7EB',
                      focusBorderColor: COLORS.primary
                    }}
                    placeholder="Enter patient's phone number"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: COLORS.darkGray }}
                    htmlFor="title"
                  >
                    Report Title
                  </label>
                  <input
                    type="text"
                    id="title"
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:outline-none"
                    style={{
                      borderColor: '#E5E7EB',
                      focusBorderColor: COLORS.primary
                    }}
                    placeholder="Enter report title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: COLORS.darkGray }}
                    htmlFor="description"
                  >
                    Description (Optional)
                  </label>
                  <textarea
                    id="description"
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:outline-none"
                    style={{
                      borderColor: '#E5E7EB',
                      focusBorderColor: COLORS.primary
                    }}
                    placeholder="Enter report description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows="3"
                  />
                </div>

                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: COLORS.darkGray }}
                    htmlFor="file-upload"
                  >
                    Report File (PDF/DOC/Image)
                  </label>
                  <input
                    type="file"
                    id="file-upload"
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:outline-none"
                    style={{
                      borderColor: '#E5E7EB',
                      focusBorderColor: COLORS.primary
                    }}
                    onChange={handleFileChange}
                    required
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  />
                </div>

                {successMessage && (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="p-4 rounded-lg"
                    style={{ backgroundColor: '#D1FAE5', color: '#065F46' }}
                  >
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      {successMessage}
                    </div>
                  </motion.div>
                )}

                {errorMessage && (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="p-4 rounded-lg"
                    style={{ backgroundColor: '#FEE2E2', color: '#B91C1C' }}
                  >
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                      {errorMessage}
                    </div>
                  </motion.div>
                )}

                <button
                  type="submit"
                  className="w-full py-3 px-6 rounded-lg font-medium flex items-center justify-center"
                  style={{ 
                    backgroundColor: COLORS.primary,
                    color: COLORS.white
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Uploading...
                    </>
                  ) : (
                    'Upload Medical Report'
                  )}
                </button>
              </form>
            </motion.div>
          )}

          {/* Search Tab */}
          {activeTab === 'search' && (
            <div className="space-y-8">
              {/* Search Form */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-white p-8 rounded-xl shadow-lg border border-gray-100"
              >
                <h2 className="text-2xl font-bold mb-6" style={{ color: COLORS.darkGray }}>
                  Search Medical Reports
                </h2>
                
                <form onSubmit={handleSearchSubmit} className="space-y-6">
                  <div>
                    <label
                      className="block text-sm font-medium mb-2"
                      style={{ color: COLORS.darkGray }}
                      htmlFor="search-phone-number"
                    >
                      Patient Phone Number
                    </label>
                    <input
                      type="tel"
                      id="search-phone-number"
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:outline-none"
                      style={{
                        borderColor: '#E5E7EB',
                        focusBorderColor: COLORS.primary
                      }}
                      placeholder="Enter patient's phone number to search"
                      value={searchPhoneNumber}
                      onChange={(e) => setSearchPhoneNumber(e.target.value)}
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3 px-6 rounded-lg font-medium flex items-center justify-center"
                    style={{ 
                      backgroundColor: COLORS.primary,
                      color: COLORS.white
                    }}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Searching...
                      </>
                    ) : (
                      'Search Reports'
                    )}
                  </button>
                </form>
              </motion.div>

              {/* Results Section */}
              {reports.length > 0 && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="bg-white p-8 rounded-xl shadow-lg border border-gray-100"
                >
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold" style={{ color: COLORS.darkGray }}>
                      Reports for {searchPhoneNumber}
                    </h2>
                    <span className="text-sm" style={{ color: COLORS.darkGray }}>
                      {filteredReports.length} {filteredReports.length === 1 ? 'report' : 'reports'} found
                    </span>
                  </div>

                  {/* Date Filter */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-4" style={{ color: COLORS.darkGray }}>
                      Filter Reports by Date
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">From Date</label>
                        <input
                          type="date"
                          className="w-full px-4 py-2 border rounded-lg"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">To Date</label>
                        <input
                          type="date"
                          className="w-full px-4 py-2 border rounded-lg"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Reports List */}
                  <div className="space-y-4">
                    {filteredReports.length > 0 ? (
                      filteredReports.map((report, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="p-4 border rounded-lg"
                          style={{ borderColor: COLORS.secondaryLight }}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-medium" style={{ color: COLORS.darkGray }}>
                                {report.title || "Medical Report"}
                              </h4>
                              <p className="text-sm text-gray-500">
                                {format(parseISO(report.created_at), 'MMMM d, yyyy - h:mm a')}
                              </p>
                              {report.description && (
                                <p className="text-sm mt-1" style={{ color: COLORS.darkGray }}>
                                  {report.description}
                                </p>
                              )}
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleDownloadReport(report.file)}
                                className="px-4 py-2 rounded-lg flex items-center"
                                style={{ 
                                  backgroundColor: COLORS.secondary,
                                  color: COLORS.darkGray
                                }}
                              >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                                </svg>
                                Download
                              </button>
                              <button
                                onClick={() => handleDeleteReport(report.id)}
                                className="px-4 py-2 rounded-lg flex items-center"
                                style={{ 
                                  backgroundColor: '#FEE2E2',
                                  color: '#B91C1C'
                                }}
                              >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                </svg>
                                Delete
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      ))
                    ) : (
                      <div className="p-4 text-center" style={{ color: COLORS.darkGray }}>
                        No reports match your filter criteria
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {errorMessage && reports.length === 0 && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="p-6 rounded-lg border-2"
                  style={{ 
                    borderColor: COLORS.primaryLight,
                    backgroundColor: COLORS.lightGray
                  }}
                >
                  <div className="flex items-center mb-4">
                    <svg className="w-8 h-8 mr-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                    <h3 className="text-xl font-bold" style={{ color: COLORS.darkGray }}>
                      No Reports Found
                    </h3>
                  </div>
                  <p className="mb-6" style={{ color: COLORS.darkGray }}>
                    {errorMessage}
                  </p>
                </motion.div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MedicalReportManagement;